import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Grid } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { serveFpsMultiSiteSearchExperimentM2 } from '@atlassian/search-common/experiments';

import { useAppContext } from '../../common/ui/app-context';
import { useBootstrap } from '../../controllers/store/bootstrap';
import { useProducts } from '../../controllers/store/search';

import {
	LiteAppFilterBar,
	LiteShowAllAppsButton,
	PromotedAppsProvider,
} from './filters/lite-app-filter-bar';
import { useElementWidth, useScrollState } from './lite-header-hooks';

/**
 * Minimum width (in px) required for the filters section in combined row mode.
 * This accounts for the dropdown toggle button (~40px) plus at least one inline
 * filter (~200px) and some padding.
 */
const MIN_FILTERS_SECTION_WIDTH = 300;

/**
 * Estimated width (in px) per app pill in the app filter bar. Each pill is a
 * button containing an icon + label, typically 80–120px. We use a conservative
 * estimate to ensure we don't show the combined row when there isn't enough
 * space.
 */
const ESTIMATED_APP_PILL_WIDTH = 110;

/**
 * Fixed overhead in the app filter bar (in px): the "All" pill (~50px), the
 * "More apps" button (~90px when visible), gaps between pills (~12px each), and
 * the grid column gap between the app bar and filters section (16px).
 */
const APP_FILTER_BAR_FIXED_OVERHEAD = 200;

type LiteHeaderProps = {
	input: React.ReactNode;
	filters: React.ReactNode;
	/**
	 * Optional feedback button rendered in the header. Its position depends on
	 * the layout:
	 * - 2-row: at the far right of the filters row.
	 * - Combined row (limited apps): at the far right of the container.
	 * - Single app: at the far right of the container.
	 */
	feedback?: React.ReactNode;
	/** Multi-site Site Selector. Only supplied when the tenant has more than one site. */
	siteSelector?: React.ReactNode;
};

const styles = cssMap({
	base: {
		position: 'sticky',
		top: '0',
		backgroundColor: token('elevation.surface'),
		width: '100%',
		backgroundClip: 'padding-box',
		zIndex: 200,
	},
	keyline: {
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	contentContainer: {
		margin: 'auto',
		maxWidth: '100%',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		alignItems: 'space-between',
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
	},
	// 2-row grid: app filter bar on row 1, filters on row 2.
	gridTwoRow: {
		gridTemplateAreas: '"input" "appFilterBar" "filters"',
		gridTemplateColumns: '1fr',
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
	},
	filtersRowContent: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		// Constant gap above the filters row.
		paddingTop: token('space.150'),
	},
	filtersRowFiltersArea: {
		flex: '1 1 0',
		minWidth: '0px',
	},
	filtersRowFeedbackArea: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		flex: '0 0 auto',
	},
	inputGridArea: {
		gridArea: 'input',
		display: 'flex',
		alignItems: 'center',
		// Provides the input → app filter bar gap (non-nav4 only).
		marginBottom: token('space.200'),
	},
	appFilterBarGridArea: {
		gridArea: 'appFilterBar',
		minWidth: '0px',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},
	appFilterBarScroll: {
		minWidth: '0px',
		flex: '1 1 0',
		// Clip overflow so pills don't flow into the "More apps" button.
		overflow: 'hidden',
	},
	filtersGridArea: {
		gridArea: 'filters',
	},
	gridSingleApp: {
		gridTemplateAreas: '"input . ." "filters . feedback"',
		gridTemplateColumns: '1fr auto auto',
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
	},
	gridCombinedRow: {
		gridTemplateAreas: '"input . . ." "appFilterBar filtersDivider filters feedback"',
		gridTemplateColumns: 'auto auto 1fr auto',
		columnGap: token('space.150'),
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
	},
	feedbackGridArea: {
		gridArea: 'feedback',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
	},
	filtersDivider: {
		gridArea: 'filtersDivider',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRightStyle: 'solid',
		alignSelf: 'stretch',
		marginBlock: token('space.100'),
	},
	siteSelectorArea: {
		flex: '0 0 auto',
		display: 'flex',
		alignItems: 'center',
	},
	verticalDivider: {
		flex: '0 0 auto',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRightStyle: 'solid',
		alignSelf: 'stretch',
		marginBlock: token('space.100'),
	},
	// Single-app layout: lays the Site Selector out ahead of the filters, since that layout has no
	// app pill row for it to lead.
	singleAppFiltersRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},
});

export const LiteHeader = ({
	input,
	filters,
	feedback,
	siteSelector,
}: LiteHeaderProps): React.JSX.Element => {
	const { isNav4Enabled } = useAppContext();
	const [{ products, primaryProduct }] = useBootstrap();
	const { availableProductsForSelectedSites } = useProducts();
	const productsForLiteHeader = serveFpsMultiSiteSearchExperimentM2({ primaryProduct })
		? availableProductsForSelectedSites
		: products;

	const isSingleApp = productsForLiteHeader.length <= 1;
	const showSiteSelector = Boolean(siteSelector);

	// Track the content container width. Drives the combined-row vs 2-row
	// layout decision below.
	const { width: containerWidth, setNode: containerRefCallback } = useElementWidth();

	// Track whether the page has been scrolled away from the top so we can
	// render the sticky header's bottom-border keyline only while scrolled.
	const { isScrolled } = useScrollState();

	// Estimate the app filter bar's natural width based on the number of
	// connected products. We can't reliably measure the bar's intrinsic width
	// because in the 2-row grid layout it stretches to fill 1fr, and
	// scrollWidth reports the stretched size. Using a per-pill estimate avoids
	// this measurement problem while adapting to the number of connected apps.
	const estimatedAppBarWidth =
		productsForLiteHeader.length * ESTIMATED_APP_PILL_WIDTH + APP_FILTER_BAR_FIXED_OVERHEAD;

	// Determine whether there's enough remaining space in the container for
	// the filters section alongside the app filter bar.
	// When containerWidth is 0 (not yet measured), default to combined row
	// so the layout is immediately correct on wide viewports.
	const remainingWidth = containerWidth - estimatedAppBarWidth;
	const hasSufficientWidth = containerWidth === 0 || remainingWidth >= MIN_FILTERS_SECTION_WIDTH;
	// The combined row is never used when the Site Selector is displayed.
	const isCombinedRow = !isSingleApp && !showSiteSelector && hasSufficientWidth;

	return (
		<>
			<Box xcss={cx(styles.base, isScrolled && styles.keyline)} testId="search-page-header">
				<Box xcss={styles.contentContainer} ref={containerRefCallback}>
					{isSingleApp ? (
						// rowGap=0 in nav4 mode — the grid has 2 template rows but only
						// one is rendered, so a non-zero gap would appear above the filters.
						<Grid
							xcss={styles.gridSingleApp}
							columnGap="space.200"
							rowGap={isNav4Enabled ? 'space.0' : 'space.200'}
						>
							{!isNav4Enabled && <Box xcss={styles.inputGridArea}>{input}</Box>}
							<Box xcss={styles.filtersGridArea}>
								{siteSelector ? (
									<Box xcss={styles.singleAppFiltersRow}>
										<Box xcss={styles.siteSelectorArea}>{siteSelector}</Box>
										<Box xcss={styles.verticalDivider} />
										<Box xcss={styles.filtersRowFiltersArea}>{filters}</Box>
									</Box>
								) : (
									filters
								)}
							</Box>
							{feedback && <Box xcss={styles.feedbackGridArea}>{feedback}</Box>}
						</Grid>
					) : isCombinedRow ? (
						// rowGap=0 in nav4 mode — same reason as gridSingleApp above.
						<Grid
							xcss={styles.gridCombinedRow}
							rowGap={isNav4Enabled ? 'space.0' : 'space.200'}
							testId="lite-header-combined-row"
						>
							{!isNav4Enabled && <Box xcss={styles.inputGridArea}>{input}</Box>}
							<PromotedAppsProvider>
								<Box xcss={styles.appFilterBarGridArea}>
									<Box xcss={styles.appFilterBarScroll}>
										<LiteAppFilterBar />
									</Box>
									<LiteShowAllAppsButton />
								</Box>
							</PromotedAppsProvider>
							<Box xcss={styles.filtersDivider} />
							<Box xcss={styles.filtersGridArea}>{filters}</Box>
							{feedback && <Box xcss={styles.feedbackGridArea}>{feedback}</Box>}
						</Grid>
					) : (
						<Grid xcss={styles.gridTwoRow} rowGap="space.0" columnGap="space.0">
							{!isNav4Enabled && <Box xcss={styles.inputGridArea}>{input}</Box>}
							<PromotedAppsProvider>
								<Box xcss={styles.appFilterBarGridArea}>
									{siteSelector && (
										<>
											<Box xcss={styles.siteSelectorArea}>{siteSelector}</Box>
											<Box xcss={styles.verticalDivider} />
										</>
									)}
									<Box xcss={styles.appFilterBarScroll}>
										<LiteAppFilterBar />
									</Box>
									<LiteShowAllAppsButton />
								</Box>
								<Box xcss={styles.filtersGridArea}>
									<Box xcss={styles.filtersRowContent}>
										<Box xcss={styles.filtersRowFiltersArea}>{filters}</Box>
										{feedback && <Box xcss={styles.filtersRowFeedbackArea}>{feedback}</Box>}
									</Box>
								</Box>
							</PromotedAppsProvider>
						</Grid>
					)}
				</Box>
			</Box>
			{isNav4Enabled && <VisuallyHidden>{input}</VisuallyHidden>}
		</>
	);
};
